import React, { useEffect, useState } from 'react'
import { Box, Fab } from '@mui/material'
import { useSessionContext } from 'session'
import { type Chamber, type State, type TableEntity, type UsFilters, UsTable, useUsPrefixLuts } from 'components'
import {
  type LeadershipOfficer,
  type LeadershipOfficerFilterResults,
  leadershipOfficerListFilter, type PersonBasicInformation,
  type Role
} from 'services'
import { LeadershipOfficerFilters } from 'components'
import { type Row } from 'react-table'
import { generateLegalName } from 'helpers'
import AddIcon from '@mui/icons-material/Add'

export interface LeadershipOfficerFilter extends UsFilters {
  state: State | null
  chamber: Chamber | null
  role: Role | null
  name: string | null
}

export const LeadershipOfficerListPage: React.FC = () => {
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const [officers, setOfficers] = useState<TableEntity[]>([])
  const initialState: LeadershipOfficerFilter = {
    state: null,
    chamber: null,
    role: null,
    name: '',
    page: 1,
    order_field: 'chamber',
    order_direction: 'ASC'
  }

  const defaultAdditionalColumns = [
    { Header: 'Leader/Officer Name', accessor: 'ofcname', order_field: 'ofcname' },
    { Header: 'State', accessor: 'state.desc', order_field: 'state' },
    { Header: 'Chamber', accessor: 'legchamber.desc', order_field: 'chamber' },
    { Header: 'Type', accessor: 'role.desc', order_field: 'role' },
    {
      Header: 'Current Leader/Office Holder',
      accessor: 'person',
      order_field: 'last_name',
      Cell: ({ row }: { row: Row<LeadershipOfficer> }) => {
        const person = row.original.person
        if (person.lastName === 'Vacant') return person.lastName
        return generateLegalName(person as PersonBasicInformation, usPrefixLutValues.prefixes)
      }
    }
  ]

  const [filters, setFilters] = useState<LeadershipOfficerFilter>(initialState)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<LeadershipOfficerFilter>(initialState)
  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const { openNewTab } = useSessionContext()

  useEffect(() => {
    changeModuleName('Leadership Officers')
    changeUniqueId('')
  }, [changeModuleName])

  const updateFilters = (filters: any): void => {
    setFilters(prevState => ({ ...prevState, ...filters }))
  }

  const cleanFilter = (): void => {
    const diff = JSON.stringify(initialState) === JSON.stringify(lastQueriedFilters)
    setFilters(initialState)
    if (!diff) { setLoadResultWithFilters(prevState => !prevState) }
  }

  const fetchOfficers = async (): Promise<LeadershipOfficerFilterResults> => {
    setLoading(true)

    try {
      const params: any = {
        chamber: filters.chamber != null ? filters.chamber.id : undefined,
        firstName: filters.firstName != null ? filters.firstName : undefined,
        lastName: filters.lastName != null ? filters.lastName : undefined,
        role: filters.role != null ? filters.role.id : undefined,
        state: filters.state != null ? filters.state.id : undefined,
        page: filters.page !== 0 ? filters.page : undefined,
        order_by: filters.order_field,
        order_direction: filters.order_direction
      }
      return await leadershipOfficerListFilter(params)
    } catch (error) {
      console.error(error)
      return { data: [], page_number: 1, page_size: 1, total_pages: 1, total_items: 0 }
    }
  }

  const updateOfficerTableWithFilter = async (): Promise<void> => {
    fetchOfficers().then(response => {
      setOfficers(response.data)
      setPageCount(response.total_pages)
      setTotalCount(response.total_items)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) { updateFilters({ page: response.page_number }) }
    }).catch(reason => {
      console.log(reason)
      setOfficers([])
      setPageCount(0)
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    const updateTablePage = async (): Promise<void> => {
      await updateOfficerTableWithFilter()
    }
    void updateTablePage()
  }, [loadResultWithFilters])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Box width='97%' sx={{ pb: 1 }}>
        <LeadershipOfficerFilters
          filters={filters}
          updateFilters={updateFilters}
          doFilter={updateOfficerTableWithFilter}
          cleanFilters={cleanFilter}
        />
        <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
          <Fab color="primary" aria-label="add" onClick={() => { openNewTab('/us/leadershipOfficers/create') }}>
            <AddIcon />
          </Fab>
        </Box>
        <UsTable
          entities={officers}
          orderField={filters.order_field}
          orderDirection={filters.order_direction}
          loading={loading}
          openDelete={() => { alert('Enter Leadership Officer edit page to delete!') }}
          pageCount={pageCount}
          totalCount={totalCount}
          triggerReload={() => { setLoadResultWithFilters(prevState => !prevState) }}
          openEdit={(entity) => {
            entity.id != null
              ? openNewTab(`/us/leadershipOfficers/edit/${String(entity.id)}`)
              : alert('No ID on this Officer 🤔')
          }}
          filters={filters}
          updateFilters={updateFilters}
          additionalColumns={defaultAdditionalColumns}
        />
      </Box>
    </Box>
  )
}
